import { engineBaseUrl } from "../lib/engine-base"
import { http } from "../lib/http"
import { getCsrfToken } from "./auth"

/**
 * OTP sign-in client for the RAM-OS engine.
 *
 * - `getOtpChannels` / `requestOtp` go through the typed `http()`
 *   boundary (JSON in/out, `credentials: "include"`).
 * - `verifyOtp` posts to the Auth.js `otp` credentials callback as
 *   form-urlencoded. CRITICAL: the `X-Auth-Return-Redirect` request
 *   header is what makes @auth/core (v0.37) answer with an HTTP **200 +
 *   JSON `{ url }`** instead of a 302 redirect — both on success
 *   (`{ url }` with the session `Set-Cookie`) and on a wrong/expired
 *   code (`{ url: "...?error=CredentialsSignin" }`). Without it the
 *   callback 302s; behind a reverse proxy / tunnel the browser then
 *   tries to *follow* that cross-origin/`http://` Location and the fetch
 *   dies with "Failed to fetch" even though sign-in actually succeeded.
 *   Failure is detected by parsing the returned `url` for `error=`,
 *   never by HTTP status alone.
 */

export const otpQueryKeys = {
	all: ["otp"] as const,
	channels: () => [...otpQueryKeys.all, "channels"] as const,
}

export const getOtpChannels = async (): Promise<{ email: boolean; whatsapp: boolean }> =>
	http<{ email: boolean; whatsapp: boolean }>("/api/auth/otp/channels")

export const requestOtp = async (
	identifier: string,
	channel: "email" | "whatsapp",
): Promise<void> => {
	await http("/api/auth/otp/request", { method: "POST", json: { identifier, channel } })
}

export const verifyOtp = async (identifier: string, code: string): Promise<void> => {
	const csrfToken = await getCsrfToken()
	const res = await fetch(`${engineBaseUrl}/api/auth/callback/otp`, {
		method: "POST",
		credentials: "include",
		// `X-Auth-Return-Redirect` flips @auth/core from a 302 to a 200 + JSON
		// `{ url }` response (see the file header). `redirect: "manual"` is a
		// belt-and-braces guard so a stray 3xx never gets followed cross-origin.
		redirect: "manual",
		headers: {
			"Content-Type": "application/x-www-form-urlencoded",
			Accept: "application/json",
			"X-Auth-Return-Redirect": "1",
		},
		body: new URLSearchParams({
			identifier,
			code,
			csrfToken,
			callbackUrl: "/",
			json: "true",
		}).toString(),
	})
	if (!res.ok) throw new Error(`Sign-in failed (HTTP ${res.status})`)
	// Auth.js credentials FAILURE is a 200 with { url: "...?error=CredentialsSignin" }.
	const data = (await res.json().catch(() => ({}))) as { url?: string }
	if (typeof data.url === "string" && data.url.includes("error=")) {
		throw new Error("That code is invalid or expired.")
	}
}
